import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Breadcrumbs,
  Link,
  Alert,
  Autocomplete,
  useTheme,
  alpha,
} from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import {
  NavigateNext as NavigateNextIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  AddCircleOutline as CreateIcon,
} from '@mui/icons-material';
import { Link as RouterLink, useNavigate, useParams, useLocation } from 'react-router-dom';
import wineRegionService, { WineRegion } from '../../../services/wineRegionService';
import { getCountryName } from '../../../utils/countryUtils';

// Sentinel type for create-on-the-fly option
interface NewRegionOption {
  _id: '__new__';
  name: string;
  __isNew__: true;
  inputValue: string;
}
type ParentOption = WineRegion | NewRegionOption;
const isNewOption = (o: ParentOption): o is NewRegionOption => (o as NewRegionOption).__isNew__ === true;
const filterParentOptions = createFilterOptions<ParentOption>();

interface FormData {
  name: string;
  localName?: string;
  parent?: string;
}

interface FormErrors {
  name?: string;
  localName?: string;
  parent?: string;
}

const RegionForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { regionId } = useParams<{ regionId?: string }>();
  const isEditMode = Boolean(regionId);

  const queryParams = new URLSearchParams(location.search);
  const parentIdFromQuery = queryParams.get('parent');

  const [formData, setFormData] = useState<FormData>({
    name: '',
    localName: '',
    parent: parentIdFromQuery || undefined,
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [needsReview, setNeedsReview] = useState<boolean>(false);
  const theme = useTheme();

  const reviewFieldSx = (isEmpty: boolean) =>
    needsReview && isEmpty
      ? { '& .MuiOutlinedInput-root': { backgroundColor: alpha(theme.palette.error.main, 0.07) } }
      : undefined;

  // Parent selector state (edit mode)
  const [selectedParent, setSelectedParent] = useState<WineRegion | null>(null);
  const [parentInputValue, setParentInputValue] = useState('');
  const [parentOptions, setParentOptions] = useState<WineRegion[]>([]);
  const [loadingParents, setLoadingParents] = useState(false);
  const [creatingParent, setCreatingParent] = useState(false);

  // Create mode state
  const [parentRegion, setParentRegion] = useState<WineRegion | null>(null);
  const [hierarchyNames, setHierarchyNames] = useState<string[]>([]);
  const [countries, setCountries] = useState<WineRegion[]>([]);
  const [loadingCountries, setLoadingCountries] = useState<boolean>(false);

  // Load region data in edit mode
  useEffect(() => {
    const fetchData = async () => {
      if (isEditMode && regionId) {
        setLoading(true);
        try {
          const region = await wineRegionService.getWineRegion(regionId);

          setFormData({
            name: region.name,
            localName: region.localName || '',
            parent: region.parent,
          });
          setHierarchyNames(region.hierarchyNames || []);
          setNeedsReview(region.needsReview ?? false);

          // Backend does not return parent/parentId directly.
          // Derive the parent ID from hierarchyPath: second-to-last entry when depth > 1.
          const hp = region.hierarchyPath ?? [];
          const resolvedParentId =
            region.parent ??
            region.parentId ??
            (hp.length > 1 ? hp[hp.length - 2] : undefined);

          if (resolvedParentId) {
            const parent = await wineRegionService.getWineRegion(resolvedParentId);
            setSelectedParent(parent);
            setParentInputValue(getParentDisplayName(parent));
          }
        } catch (error) {
          console.error('Failed to fetch region:', error);
        } finally {
          setLoading(false);
        }
      } else if (parentIdFromQuery) {
        setLoading(true);
        try {
          const parent = await wineRegionService.getWineRegion(parentIdFromQuery);
          setParentRegion(parent);
        } catch (error) {
          console.error('Failed to fetch parent region:', error);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchData();
  }, [isEditMode, regionId, parentIdFromQuery]);

  // Load countries for create-mode parent dropdown
  useEffect(() => {
    if (!isEditMode && !parentIdFromQuery) {
      setLoadingCountries(true);
      wineRegionService
        .getCountries(1, 100)
        .then((r) => setCountries(r.data))
        .catch(() => {})
        .finally(() => setLoadingCountries(false));
    }
  }, [isEditMode, parentIdFromQuery]);

  // Async parent search (edit mode)
  useEffect(() => {
    if (!isEditMode) return;
    if (parentInputValue.length < 2) {
      setParentOptions([]);
      return;
    }
    let active = true;
    setLoadingParents(true);
    wineRegionService
      .searchWineRegions(parentInputValue, 1, 15)
      .then((resp) => {
        if (active) {
          // Exclude self to prevent self-parenting
          setParentOptions(resp.data.filter((r) => r._id !== regionId));
        }
      })
      .catch(() => {})
      .finally(() => { if (active) setLoadingParents(false); });
    return () => { active = false; };
  }, [parentInputValue, isEditMode, regionId]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name) {
      setFormData({ ...formData, [name]: value });
      if (errors[name as keyof FormErrors]) {
        setErrors({ ...errors, [name]: undefined });
      }
    }
  };

  const handleSelectChange = (event: any) => {
    const name = event.target.name as string;
    const value = event.target.value;
    if (name) {
      setFormData({ ...formData, [name]: value });
      if (errors[name as keyof FormErrors]) {
        setErrors({ ...errors, [name]: undefined });
      }
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!isEditMode && !parentIdFromQuery && !formData.parent) {
      newErrors.parent = 'Parent region is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getParentDisplayName = useCallback((region: WineRegion) => {
    if (region.hierarchyNames && region.hierarchyNames.length > 1) {
      return region.hierarchyNames.join(' › ');
    }
    return /^[A-Z]{2}$/.test(region.name) ? getCountryName(region.name) : region.name;
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSaving(true);
    try {
      if (isEditMode && regionId) {
        // Auto-accept only when hierarchy traces back to a country (2-letter code at root)
        const parentHasCountry =
          selectedParent?.hierarchyNames != null &&
          selectedParent.hierarchyNames.length > 0 &&
          /^[A-Z]{2}$/.test(selectedParent.hierarchyNames[0]);
        const isComplete = !!formData.name.trim() && selectedParent !== null && parentHasCountry;
        const payload = {
          name: formData.name,
          localName: formData.localName,
          parentId: selectedParent ? selectedParent._id : null,
          ...(needsReview && isComplete ? { needsReview: false } : {}),
        };
        await wineRegionService.updateWineRegion(regionId, payload as any);
        navigate(`/admin/regions/${regionId}`);
      } else {
        const parent = parentIdFromQuery || formData.parent;
        if (!parent) throw new Error('Parent region is required');
        const newRegion = await wineRegionService.createWineRegion({
          name: formData.name,
          localName: formData.localName,
          parentId: parent,
        });
        navigate(`/admin/regions/${newRegion._id}`);
      }
    } catch (error) {
      console.error('Failed to save region:', error);
      alert('Failed to save region. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 2,
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Typography variant="h4" component="h1" gutterBottom>
          {isEditMode ? 'Edit Wine Region' : 'Create Wine Region'}
        </Typography>

        {needsReview && isEditMode && (() => {
          const issues: string[] = [];
          if (!formData.name.trim()) issues.push('Name is missing');
          if (!selectedParent) issues.push('Parent region is not set — this region is standalone');
          return (
            <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2, backgroundColor: alpha(theme.palette.warning.main, 0.08), color: 'text.primary', '& .MuiAlert-icon': { color: theme.palette.warning.main } }}>
              <strong>Needs review</strong> — this record was auto-created during import.
              {issues.length > 0 && (
                <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2 }}>
                  {issues.map((issue) => <li key={issue}>{issue}</li>)}
                </Box>
              )}
              {issues.length === 0 && ' All required fields are filled — saving will mark this as complete.'}
            </Alert>
          );
        })()}

        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
          sx={{ mb: 3 }}
        >
          <Link component={RouterLink} to="/admin/regions" color="inherit" underline="hover">
            All Regions
          </Link>
          {parentRegion && (
            <Link
              component={RouterLink}
              to={`/admin/regions/${parentRegion._id}`}
              color="inherit"
              underline="hover"
            >
              {parentRegion.name}
            </Link>
          )}
          <Typography color="textPrimary">
            {isEditMode ? 'Edit Region' : 'Create Region'}
          </Typography>
        </Breadcrumbs>

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Region Name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            margin="normal"
            error={Boolean(errors.name)}
            helperText={errors.name}
            required
            sx={reviewFieldSx(!formData.name.trim())}
          />

          {isEditMode && hierarchyNames.length > 1 && (
            <Box
              sx={{
                mt: 1,
                mb: 1,
                px: 2,
                py: 1.5,
                borderRadius: 1,
                backgroundColor: 'action.hover',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ mb: 0.5, fontWeight: 600, letterSpacing: 0.5 }}
              >
                CURRENT HIERARCHY PATH
              </Typography>
              <Typography variant="body2" color="text.primary">
                {hierarchyNames.join(' › ')}
              </Typography>
            </Box>
          )}

          <TextField
            fullWidth
            label="Local Name"
            name="localName"
            value={formData.localName || ''}
            onChange={handleInputChange}
            margin="normal"
            error={Boolean(errors.localName)}
            helperText={errors.localName || 'Name in local language (optional)'}
          />

          {/* Parent selector — searchable in edit mode, dropdown in create mode */}
          {isEditMode ? (
            <>
              {creatingParent && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                  <CircularProgress size={14} />
                  <Typography variant="caption" color="text.secondary">Creating region…</Typography>
                </Box>
              )}
              <Autocomplete<ParentOption>
                sx={{ mt: 2, ...reviewFieldSx(!selectedParent) }}
                options={parentOptions as ParentOption[]}
                loading={loadingParents}
                value={selectedParent}
                inputValue={parentInputValue}
                getOptionLabel={(option) => isNewOption(option) ? option.inputValue : getParentDisplayName(option as WineRegion)}
                isOptionEqualToValue={(option, value) => {
                  if (isNewOption(option) || isNewOption(value)) return false;
                  return option._id === value._id;
                }}
                filterOptions={(options, params) => {
                  // API results already filtered — just append create option if no exact match
                  const { inputValue } = params;
                  const filtered = options.filter(o => !isNewOption(o));
                  const exists = filtered.some(o =>
                    (o as WineRegion).name.toLowerCase() === inputValue.toLowerCase()
                  );
                  if (inputValue.trim() !== '' && !exists) {
                    filtered.push({
                      _id: '__new__',
                      name: `Create "${inputValue}"`,
                      __isNew__: true,
                      inputValue: inputValue.trim(),
                    });
                  }
                  return filtered;
                }}
                onInputChange={(_, value) => setParentInputValue(value)}
                onChange={async (_, value) => {
                  if (!value) {
                    setSelectedParent(null);
                    setParentInputValue('');
                    return;
                  }
                  if (isNewOption(value)) {
                    setCreatingParent(true);
                    try {
                      const created = await wineRegionService.createWineRegion({
                        name: value.inputValue,
                        needsReview: true,
                      } as any);
                      setSelectedParent(created);
                      setParentInputValue(getParentDisplayName(created));
                      setParentOptions(prev => [created, ...prev]);
                    } catch {
                      alert('Failed to create region. Please try again.');
                    } finally {
                      setCreatingParent(false);
                    }
                    return;
                  }
                  setSelectedParent(value as WineRegion);
                  setParentInputValue(getParentDisplayName(value as WineRegion));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Parent Region"
                    helperText="Search to change parent. Clear to make this a top-level region."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingParents ? <CircularProgress color="inherit" size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => {
                  if (isNewOption(option)) {
                    return (
                      <li {...props} key="__new__">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main' }}>
                          <CreateIcon fontSize="small" />
                          <Typography variant="body2" fontWeight={600} color="primary.main">
                            {option.name}
                          </Typography>
                        </Box>
                      </li>
                    );
                  }
                  const region = option as WineRegion;
                  return (
                    <li {...props} key={region._id}>
                      <Box>
                        <Typography variant="body2">{region.name}</Typography>
                        {region.hierarchyNames && region.hierarchyNames.length > 1 && (
                          <Typography variant="caption" color="text.secondary">
                            {region.hierarchyNames.join(' › ')}
                          </Typography>
                        )}
                      </Box>
                    </li>
                  );
                }}
              />
            </>
          ) : !parentIdFromQuery ? (
            <FormControl fullWidth margin="normal" error={Boolean(errors.parent)}>
              <InputLabel id="parent-label">Parent Region</InputLabel>
              <Select
                labelId="parent-label"
                name="parent"
                value={formData.parent || ''}
                onChange={handleSelectChange}
                label="Parent Region"
                required
              >
                {loadingCountries ? (
                  <MenuItem disabled>Loading countries...</MenuItem>
                ) : (
                  countries.map((country) => (
                    <MenuItem key={country._id} value={country._id}>
                      {/^[A-Z]{2}$/.test(country.name) ? getCountryName(country.name) : country.name}
                    </MenuItem>
                  ))
                )}
              </Select>
              {errors.parent && <FormHelperText>{errors.parent}</FormHelperText>}
            </FormControl>
          ) : (
            <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
              This region will be created as a child of{' '}
              <strong>
                {parentRegion
                  ? /^[A-Z]{2}$/.test(parentRegion.name)
                    ? getCountryName(parentRegion.name)
                    : parentRegion.name
                  : '…'}
              </strong>
            </Typography>
          )}

          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Region'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<CancelIcon />}
              onClick={() =>
                navigate(
                  isEditMode
                    ? `/admin/regions/${regionId}`
                    : parentRegion
                    ? `/admin/regions/${parentRegion._id}`
                    : '/admin/regions'
                )
              }
            >
              Cancel
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default RegionForm;
